/**
 * Getting a Microsoft ADMIN CONSENT out of the browser.
 *
 * NOT `popupAuthCode`, THOUGH IT IS THE SAME SHAPE. That helper waits for an
 * authorization `code` and refuses a reply without one. An admin-consent redirect
 * carries none: Microsoft sends back `tenant`, `state` and `admin_consent=True`
 * (or `error`), and no token is ever issued to the browser. Bending the shared
 * helper to accept both would loosen the check the calendar and the mailbox rely
 * on, so this is its own, and keeps the three properties that matter:
 *
 *   * the popup is opened BEFORE the request, or every browser blocks it;
 *   * the reply is accepted only from OUR origin and only with the `state` the
 *     server minted, so a forged message cannot complete someone else's consent;
 *   * closing the popup is a cancel, not a hang.
 *
 * The relay page posts the reply; the CALLER then hands it to
 * `GET /v1/phone/callback` with the admin's own session. `state` is single use and
 * bound to the user who began, so nobody else's session can spend it.
 */
import { beginPhoneConnection } from '@biasafe.queries/phone-connect/phone-connect';
import { isCallbackMessage } from '@/shared/oauth/popupAuthCode';

export const PHONE_CALLBACK_SOURCE = 'biasafe-phone-callback';

/** Registered with the Microsoft app, for every origin, before this ships. */
export const PHONE_CALLBACK_PATH = '/auth/microsoft/phone-callback';

/** What the relay page posts back. Every field is the query string's. */
export interface PhoneConsentMessage {
  source: string;
  state?: string;
  /** The signed Microsoft token from the sign-in redirect's fragment. The ONLY
   * thing the server trusts for which tenant this is. */
  id_token?: string;
  tenant?: string;
  admin_consent?: string;
  error?: string;
}

export interface PhoneConsent {
  state: string;
  id_token?: string;
  tenant?: string;
  admin_consent?: string;
  error?: string;
}

export function isPhoneConsentMessage(
  data: unknown,
): data is PhoneConsentMessage {
  return isCallbackMessage(PHONE_CALLBACK_SOURCE, data);
}

/**
 * Wait for the relay page to post the reply for `expectedState` from `popup`.
 * Shared by the sign-in hop and the admin-consent hop, which come back the same way.
 */
function awaitRelay(
  popup: Window,
  expectedState: string,
): Promise<PhoneConsent> {
  return new Promise<PhoneConsent>((resolve, reject) => {
    let settled = false;
    const finish = (fn: () => void) => {
      if (settled) return;
      settled = true;
      window.removeEventListener('message', onMessage);
      window.clearInterval(closedTimer);
      fn();
    };

    const onMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      if (!isPhoneConsentMessage(event.data)) return;
      const { state, id_token, tenant, admin_consent, error } = event.data;
      finish(() => {
        popup.close();
        if (!state || state !== expectedState) {
          reject(new Error('The Microsoft reply did not match this request.'));
          return;
        }
        resolve({ state, id_token, tenant, admin_consent, error });
      });
    };

    const closedTimer = window.setInterval(() => {
      if (popup.closed) {
        finish(() => reject(new Error('Connecting was cancelled.')));
      }
    }, 500);

    window.addEventListener('message', onMessage);
  });
}

/**
 * Step 1: the admin SIGNS IN to Microsoft so the server can learn their tenant from
 * a signed token. The tenant is never read from this reply's query string.
 *
 * `tenantHint` is OPTIONAL and only for a single-tenant app: it tells the server
 * which tenant to address the sign-in at, and the server refuses a sign-in to any
 * other. Omitted, the request is exactly what it was before.
 */
export async function requestPhoneConsent(
  tenantHint?: string,
): Promise<PhoneConsent> {
  const redirectUri = `${window.location.origin}${PHONE_CALLBACK_PATH}`;

  const popup = window.open(
    '',
    'biasafe-phone-connect',
    'width=560,height=720',
  );
  if (!popup) {
    throw new Error(
      'The Microsoft window was blocked. Allow popups and try again.',
    );
  }

  let authorizeUrl: string;
  let expectedState: string;
  try {
    const started = await beginPhoneConnection(
      tenantHint ? { redirectUri, tenantHint } : { redirectUri },
    );
    authorizeUrl = started.authorizeUrl;
    expectedState = started.state;
  } catch (err) {
    popup.close();
    throw err;
  }

  popup.location.href = authorizeUrl;
  return awaitRelay(popup, expectedState);
}

/**
 * Step 2, only when the server says the app has no consent in the admin's tenant:
 * open the admin-consent page it built for THAT tenant. Resolves when Microsoft
 * redirects back; the caller then asks the server to verify.
 */
export async function requestPhoneAdminConsent(
  consentUrl: string,
): Promise<PhoneConsent> {
  const expectedState = new URL(consentUrl).searchParams.get('state') ?? '';
  const popup = window.open(
    consentUrl,
    'biasafe-phone-consent',
    'width=560,height=720',
  );
  if (!popup) {
    throw new Error(
      'The Microsoft window was blocked. Allow popups and try again.',
    );
  }
  const reply = await awaitRelay(popup, expectedState);
  if (reply.error || (reply.admin_consent ?? '').toLowerCase() !== 'true') {
    throw new Error('Microsoft did not grant access.');
  }
  return reply;
}
